import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser, useCurrentTeamId, useCurrentTeam } from "@/stores/auth";
import { useCredits } from "@/stores/credits";
import { ThemeToggle } from "@/components/atoms/ThemeToggle";
import { CreditDisplay } from "@/components/atoms/CreditDisplay";
// TODO: Re-enable notifications feature when ready
// import { NotificationDropdown } from "@/components/molecules/NotificationDropdown";
import { ProfileDropdown } from "@/components/molecules/ProfileDropdown";
import { SidePanel } from "@/components/organisms/SidePanel";
// import type { Notification } from "@/components/molecules/NotificationItem";
import primaryLogo from "@/assets/Primary Logo.svg";
import secondaryLogo from "@/assets/Secondary Logo.svg";
import { Menu, X, ChevronDown } from "lucide-react";
import { useTheme } from "@/stores/theme";
import { ROUTES } from "@/config/routes";

export const Header = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const user = useUser();
  const currentTeamId = useCurrentTeamId();
  const currentTeam = useCurrentTeam();
  const { remainingCredits } = useCredits();
  const { currentTheme } = useTheme();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  // const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const logoSvg = currentTheme === 'dark' ? secondaryLogo : primaryLogo;

  // Get team initials for avatar
  const getTeamInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // TODO: Re-enable notifications feature when ready
  // const dummyNotifications: Notification[] = [
  //   {
  //     id: "1",
  //     type: "success",
  //     title: "Campaign Complete",
  //     message:
  //       'Your "Summer Sale" campaign has been successfully completed with 2,847 messages sent.',
  //     timestamp: "2 minutes ago",
  //     isRead: false,
  //   },
  //   {
  //     id: "2",
  //     type: "warning",
  //     title: "Low Credits",
  //     message:
  //       "Your account balance is running low. Consider adding more credits to continue your campaigns.",
  //     timestamp: "15 minutes ago",
  //     isRead: false,
  //   },
  //   {
  //     id: "3",
  //     type: "info",
  //     title: "New Feature Available",
  //     message:
  //       "Check out our new analytics dashboard with advanced reporting features.",
  //     timestamp: "1 hour ago",
  //     isRead: true,
  //   },
  //   {
  //     id: "4",
  //     type: "error",
  //     title: "Message Delivery Failed",
  //     message:
  //       'Some messages in your "Weekend Promo" campaign failed to deliver. Please review the failed contacts.',
  //     timestamp: "2 hours ago",
  //     isRead: false,
  //   },
  //   {
  //     id: "5",
  //     type: "success",
  //     title: "Account Verified",
  //     message:
  //       "Your phone number has been successfully verified. You can now send messages to all contacts.",
  //     timestamp: "1 day ago",
  //     isRead: true,
  //   },
  // ];

  return (
    <>
      <header className="w-full bg-background/95 backdrop-blur-md border-b border-border shadow-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-accent transition-colors"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center justify-center">
              <img
                src={logoSvg}
                alt="Just Ping Logo"
                className="h-8 w-auto transition-all duration-300"
              />
            </div>

            {/* Team Selector - Clickable to switch teams */}
            {currentTeamId && currentTeam && (
              <>
                <div className="w-px h-6 bg-border mx-2 hidden sm:block" />

                <button
                  onClick={() => navigate(ROUTES.SELECT_TEAM)}
                  className="hidden sm:flex items-center gap-2.5 px-3 py-1.5 rounded-lg
                    bg-muted/50 hover:bg-muted border border-transparent hover:border-border
                    transition-all duration-200 group"
                >
                  {/* Team Avatar */}
                  <div className="h-7 w-7 rounded-md bg-gradient-to-br from-primary/20 to-primary/5
                    border border-primary/20 flex items-center justify-center
                    group-hover:border-primary/40 transition-colors">
                    <span className="text-xs font-bold text-primary">
                      {getTeamInitials(currentTeam.name)}
                    </span>
                  </div>

                  {/* Team Name */}
                  <span className="text-sm font-medium text-foreground max-w-[140px] truncate">
                    {currentTeam.name}
                  </span>

                  {/* Chevron */}
                  <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <CreditDisplay credits={remainingCredits} className="hidden sm:block" />
            
            <div className="w-px h-6 bg-border mx-1 hidden sm:block" />
            
            {/* TODO: Re-enable notifications feature when ready
            <NotificationDropdown
              notifications={dummyNotifications}
              isOpen={isNotificationsOpen}
              onOpenChange={setIsNotificationsOpen}
            />
            */}

            <ThemeToggle className="scale-90" />

            <ProfileDropdown
              user={user ? { ...user, profileImage: user.profileImage } : null}
              isOpen={isProfileOpen}
              onOpenChange={setIsProfileOpen}
              onLogout={logout}
            />
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full w-80 bg-background/95 backdrop-blur-md border-r border-border shadow-lg">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <img
                    src={logoSvg}
                    alt="Just Ping Logo"
                    className="h-7 w-auto transition-all duration-300"
                  />
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Mobile Team Switcher */}
            {currentTeamId && currentTeam && (
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  navigate(ROUTES.SELECT_TEAM);
                }}
                className="w-full p-4 border-b border-border flex items-center gap-3
                  hover:bg-muted/50 transition-colors text-left"
              >
                {/* Team Avatar */}
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5
                  border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-primary">
                    {getTeamInitials(currentTeam.name)}
                  </span>
                </div>

                {/* Team Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{currentTeam.name}</p>
                  <p className="text-xs text-muted-foreground">Tap to switch team</p>
                </div>

                {/* Chevron */}
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            )}

            <SidePanel className="border-0 shadow-none w-full h-[calc(100vh-73px)]" />
          </div>
        </div>
      )}
    </>
  );
};
